import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Users, DollarSign, Clock, CheckCircle2, Circle, Loader2,
  FileText, Calendar, FolderOpen, Plus, Bot, Search, X, UserPlus, Trash2, MapPin, Share2, Copy, Check, LinkIcon, Shield
} from 'lucide-react';
import { AinaaBadge } from '../components/AinaaBadge';
import { demoProjects, demoTasks, demoFiles, demoPayments } from '../data/dummyData';
import { authHeaders } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const statusIcon = (s) => {
  if (s === 'done') return <CheckCircle2 className="w-3.5 h-3.5 text-foreground" strokeWidth={2} />;
  if (s === 'in-progress') return <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" strokeWidth={2} />;
  return <Circle className="w-3.5 h-3.5 text-muted-foreground/50" strokeWidth={2} />;
};

function InviteModal({ projectId, existingIds, onInvite, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(null);

  const searchPeople = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const res = await fetch(`${API}/people${params}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setResults(data.filter(p => !existingIds.includes(p.person_id)));
      }
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [existingIds]);

  useEffect(() => {
    searchPeople('');
  }, [searchPeople]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) searchPeople(query);
      else if (query.length === 0) searchPeople('');
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchPeople]);

  const handleInvite = async (person) => {
    setInviting(person.person_id);
    try {
      const res = await fetch(`${API}/projects/${projectId}/team/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ person_id: person.person_id, role: person.role }),
      });
      if (res.ok) {
        const invite = await res.json();
        onInvite(invite);
        setResults(prev => prev.filter(p => p.person_id !== person.person_id));
      }
    } catch {
      // silently handled
    } finally {
      setInviting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="invite-modal-overlay" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[70vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="invite-modal"
      >
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">Invite to Project</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Search the AINAA network</p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} data-testid="invite-modal-close">
            <X className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>

        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, role, or skill..."
              className="pl-9 h-10 text-sm"
              data-testid="invite-search-input"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="invite-results">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : results.length > 0 ? (
            results.map(p => (
              <div key={p.person_id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card hover:bg-secondary/40 transition-colors" data-testid={`invite-person-${p.person_id}`}>
                <img
                  src={p.avatar}
                  alt={p.name}
                  className="w-9 h-9 rounded-full object-cover bg-secondary flex-shrink-0"
                  onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=e5e5e5&color=333&size=80`; }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    {p.verified && <Badge variant="secondary" className="text-[8px] h-3.5 px-1">Verified</Badge>}
                  </div>
                  <p className="text-[10px] text-muted-foreground">{p.role} · {p.location}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-muted-foreground">Trust: <span className="text-foreground font-medium">{p.trust_score}</span></span>
                    <span className="text-[10px] text-muted-foreground">{p.rate}</span>
                    {!p.available && <Badge variant="secondary" className="text-[8px] h-3.5 px-1 bg-destructive/10 text-destructive">Unavailable</Badge>}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1 flex-shrink-0"
                  onClick={() => handleInvite(p)}
                  disabled={inviting === p.person_id}
                  data-testid={`invite-btn-${p.person_id}`}
                >
                  {inviting === p.person_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" strokeWidth={2} />}
                  Invite
                </Button>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">No matching collaborators found</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}


export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [shareToken, setShareToken] = useState(project?.share_token || null);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    try {
      const res = await fetch(`${API}/projects/${id}/share`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setShareToken(data.share_token);
        const url = `${window.location.origin}/shared/${data.share_token}`;
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      }
    } catch { /* handled */ }
  };

  const revokeShare = async () => {
    try {
      await fetch(`${API}/projects/${id}/share`, { method: 'DELETE', headers: authHeaders() });
      setShareToken(null);
    } catch { /* handled */ }
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/projects/${id}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) { setProject(data); setShareToken(data.share_token || null); } })
      .catch(() => {});

    const demo = demoProjects.find(p => p.project_id === id);
    if (demo) setProject(prev => prev || demo);

    return () => { cancelled = true; };
  }, [id]);

  const loadTeam = useCallback(async () => {
    setTeamLoading(true);
    try {
      const res = await fetch(`${API}/projects/${id}/team`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTeam(data);
      }
    } catch (err) {
      console.error('Failed to load team:', err);
    } finally {
      setTeamLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleInvite = (invite) => {
    setTeam(prev => [...prev, invite]);
    setProject(prev => prev ? { ...prev, team_size: (prev.team_size || 0) + 1 } : prev);
  };

  const handleRemove = async (inviteId) => {
    try {
      const res = await fetch(`${API}/projects/${id}/team/${inviteId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        setTeam(prev => prev.filter(t => t.invite_id !== inviteId));
        setProject(prev => prev ? { ...prev, team_size: Math.max(0, (prev.team_size || 1) - 1) } : prev);
      }
    } catch {
      // silently handled
    }
  };

  const handleRoleChange = async (inviteId, newRole) => {
    try {
      await fetch(`${API}/projects/${id}/team/${inviteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ role: newRole }),
      });
      setTeam(prev => prev.map(t => t.invite_id === inviteId ? { ...t, role: newRole } : t));
    } catch {
      // silently handled
    }
  };

  const tasks = demoTasks.filter(t => t.project_id === id);
  const files = demoFiles.filter(f => f.project_id === id);
  const payments = demoPayments.filter(p => p.project_id === id);

  if (!project) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="project-detail-page">
      {/* Header */}
      <motion.div variants={fadeUp} className="mb-6">
        <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground mb-4" asChild>
          <Link to="/projects"><ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to Projects</Link>
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">{project.name}</h1>
              <Badge variant={project.status === 'active' ? 'default' : 'secondary'} className="capitalize text-[10px] h-5">{project.status}</Badge>
            </div>
            {project.ainaa_id && <span className="text-[10px] font-mono text-muted-foreground tracking-wide">{project.ainaa_id}</span>}
            <p className="text-sm text-muted-foreground">{project.description}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant={shareToken ? 'secondary' : 'outline'}
              size="sm"
              className="gap-1.5 h-9"
              onClick={shareToken ? revokeShare : handleShare}
              data-testid="share-project-btn"
            >
              {shareCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Share2 className="w-3.5 h-3.5" strokeWidth={2} />}
              {shareCopied ? 'Copied!' : shareToken ? 'Shared' : 'Share'}
            </Button>
            <Button size="sm" className="gap-1.5 h-9" asChild data-testid="ask-spectre-project-btn">
              <Link to="/spectre"><Bot className="w-3.5 h-3.5" strokeWidth={2} /> Ask Specter</Link>
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Row */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Clock, label: 'Progress', value: `${project.progress || 0}%` },
          { icon: Users, label: 'Team Size', value: project.team_size || team.length },
          { icon: DollarSign, label: 'Budget', value: project.budget ? `₹${(project.budget/100000).toFixed(1)}L` : '\u2014' },
          { icon: CheckCircle2, label: 'Tasks Done', value: `${tasks.filter(t => t.status === 'done').length}/${tasks.length}` },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl border border-border/50 bg-card">
            <s.icon className="w-4 h-4 text-muted-foreground mb-2" strokeWidth={2} />
            <p className="font-heading text-xl font-semibold">{s.value}</p>
            <p className="text-[10px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* Progress Bar */}
      <motion.div variants={fadeUp} className="mb-6">
        <div className="w-full h-2 rounded-full bg-secondary">
          <div className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all" style={{ width: `${project.progress || 0}%` }} />
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={fadeUp}>
        <Tabs defaultValue="tasks" className="w-full">
          <TabsList className="mb-4 h-9" data-testid="project-tabs">
            <TabsTrigger value="tasks" className="text-xs h-7">Tasks</TabsTrigger>
            <TabsTrigger value="team" className="text-xs h-7">Team ({team.length})</TabsTrigger>
            <TabsTrigger value="files" className="text-xs h-7">Files</TabsTrigger>
            <TabsTrigger value="budget" className="text-xs h-7">Budget</TabsTrigger>
          </TabsList>

          {/* Tasks Tab */}
          <TabsContent value="tasks" data-testid="tasks-tab">
            <div className="space-y-2">
              {tasks.length > 0 ? tasks.map(t => (
                <div key={t.task_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card" data-testid={`task-${t.task_id}`}>
                  {statusIcon(t.status)}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${t.status === 'done' ? 'text-muted-foreground line-through' : 'font-medium'}`}>{t.title}</p>
                    <p className="text-[10px] text-muted-foreground">{t.assignee_role} · Due {t.due_date}</p>
                  </div>
                  <Badge variant={t.priority === 'high' ? 'default' : 'secondary'} className="text-[10px] h-5 px-1.5 capitalize">{t.priority}</Badge>
                </div>
              )) : (
                <div className="text-center py-12">
                  <p className="text-sm text-muted-foreground">No tasks yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" data-testid="team-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground">{team.length} member{team.length !== 1 ? 's' : ''}</p>
              <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
                <UserPlus className="w-3.5 h-3.5" strokeWidth={2} /> Invite Member
              </Button>
            </div>

            {teamLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : team.length > 0 ? (
              <div className="space-y-2">
                {team.map(m => (
                  <div key={m.invite_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card group" data-testid={`team-member-${m.invite_id}`}>
                    <img
                      src={m.avatar}
                      alt={m.name}
                      className="w-9 h-9 rounded-full object-cover bg-secondary flex-shrink-0"
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=e5e5e5&color=333&size=80`; }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{m.name}</p>
                        <Badge variant="secondary" className="text-[9px] h-4 px-1.5 capitalize">{m.status}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                        <span className="font-mono">{m.ainaa_id}</span>
                        <span className="text-border">·</span>
                        <span>{m.role || m.person_role}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[hsl(36,80%,52%)]/15 text-[hsl(36,80%,42%)] text-[9px] font-medium">
                        <Shield className="w-2.5 h-2.5" strokeWidth={2.5} />{m.trust_score}
                      </span>
                      {m.rate && <span className="text-[10px] text-muted-foreground">{m.rate}</span>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemove(m.invite_id)}
                        data-testid={`remove-member-${m.invite_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-border rounded-xl">
                <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground mb-1">No team members yet</p>
                <p className="text-xs text-muted-foreground mb-4">Invite collaborators from the AINAA network</p>
                <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowInvite(true)} data-testid="invite-member-empty-btn">
                  <UserPlus className="w-3.5 h-3.5" strokeWidth={2} /> Invite Member
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" data-testid="files-tab">
            <div className="space-y-2">
              {files.length > 0 ? files.map(f => (
                <div key={f.file_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card">
                  <FolderOpen className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.name}</p>
                    <p className="text-[10px] text-muted-foreground">{f.uploaded_by} · {f.size}{f.ainaa_id && <span className="ml-1.5 font-mono">{f.ainaa_id}</span>}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{f.created_at}</span>
                </div>
              )) : (
                <div className="text-center py-12">
                  <p className="text-sm text-muted-foreground">No files yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Budget Tab */}
          <TabsContent value="budget" data-testid="budget-tab">
            <div className="space-y-2">
              {payments.length > 0 ? payments.map(p => (
                <div key={p.payment_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card">
                  <DollarSign className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-[10px] text-muted-foreground">To {p.recipient} · Due {p.due_date}{p.ainaa_id && <span className="ml-1.5 font-mono">{p.ainaa_id}</span>}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-heading font-medium">₹{(p.amount/1000).toFixed(0)}K</p>
                    <Badge
                      variant={p.status === 'paid' ? 'default' : p.status === 'overdue' ? 'destructive' : 'secondary'}
                      className="text-[9px] h-4 px-1 capitalize"
                    >
                      {p.status}
                    </Badge>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12">
                  <p className="text-sm text-muted-foreground">No budget items</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>

      <AnimatePresence>
        {showInvite && (
          <InviteModal
            projectId={id}
            existingIds={team.map(t => t.person_id)}
            onInvite={handleInvite}
            onClose={() => setShowInvite(false)}
          />
        )}
      </AnimatePresence>

    </motion.div>
  );
}
